import {
    CompanyResource,
    CompanyResourceCategory,
    CompanyResourceFileType,
    FILE_TYPE_MAP,
    TRAINING_CATEGORIES,
    RESOURCE_CATEGORIES,
    ServiceProcedure,
    ServiceProcedureConsumable,
} from '../types/companyResourceTypes';
import { PMPartsAndProcedure, LaborRate } from '../types/pmTypes';

/**
 * Infers the file type from a file name's extension.
 * Extracts the extension, looks it up in FILE_TYPE_MAP, and defaults to 'Document'
 * for unrecognized extensions.
 */
export function inferFileType(fileName: string): CompanyResourceFileType {
    const lastDot = fileName.lastIndexOf('.');
    if (lastDot === -1) return 'Document';
    const ext = fileName.slice(lastDot).toLowerCase();
    return FILE_TYPE_MAP[ext] ?? 'Document';
}

/**
 * Maps a CompanyResourceFileType to the corresponding TrainingTopic style string.
 * PDF → "PDF", Video → "Video", Image → "Poster", others → "Other".
 */
export function mapFileTypeToTopicStyle(fileType: CompanyResourceFileType): string {
    switch (fileType) {
        case 'PDF': return 'PDF';
        case 'Video': return 'Video';
        case 'Image': return 'Poster';
        default: return 'Other';
    }
}

/**
 * Returns true if the given category is training-eligible
 * (i.e., "Safety Posters" or "Training Materials").
 */
export function isTrainingCategory(category: CompanyResourceCategory): boolean {
    return TRAINING_CATEGORIES.includes(category);
}

/**
 * Returns the current effective labor rate from a list of rates.
 * The effective rate is the one with the most recent effectiveDate on or before today.
 * Returns null if no rate has an effectiveDate on or before today.
 */
export function getCurrentEffectiveRate(rates: LaborRate[]): LaborRate | null {
    const now = new Date();
    const todayISO = now.toISOString();

    const eligible = rates.filter(r => r.effectiveDate <= todayISO);
    if (eligible.length === 0) return null;

    eligible.sort((a, b) => b.effectiveDate.localeCompare(a.effectiveDate));
    return eligible[0];
}

/**
 * Finds an existing category whose name matches `name` case-insensitively.
 * Pass `excludeName` when renaming so the current category is not treated as a conflict.
 */
export function findExistingCategoryName(
    categories: string[],
    name: string,
    excludeName?: string,
): string | undefined {
    const trimmed = name.trim();
    if (!trimmed) return undefined;

    const needle = trimmed.toLowerCase();
    const exclude = excludeName?.trim().toLowerCase();

    return categories.find((category) => {
        const key = category.trim().toLowerCase();
        if (!key) return false;
        if (exclude && key === exclude) return false;
        return key === needle;
    });
}

/** True when `name` is free (or only matches `excludeName`, e.g. casing-only rename). */
export function isCategoryNameAvailable(
    categories: string[],
    name: string,
    excludeName?: string,
): boolean {
    return !findExistingCategoryName(categories, name, excludeName);
}

/**
 * Filters service items by search query and category.
 * Search matches itemName, partNumber, manufacturer, or category (case-insensitive).
 * Category filter matches the item's category exactly.
 */
export function filterServiceItems(
    items: PMPartsAndProcedure[],
    searchQuery: string,
    categoryFilter: string
): PMPartsAndProcedure[] {
    let filtered = items;

    const query = searchQuery.trim().toLowerCase();
    if (query) {
        filtered = filtered.filter(item => {
            const name = (item.itemName ?? '').toLowerCase();
            const part = (item.partNumber ?? '').toLowerCase();
            const mfr = (item.manufacturer ?? '').toLowerCase();
            const category = (item.category ?? '').toLowerCase();
            return name.includes(query) || part.includes(query) || mfr.includes(query) || category.includes(query);
        });
    }

    if (categoryFilter) {
        filtered = filtered.filter(item => item.category === categoryFilter);
    }

    return filtered;
}

/**
 * Filters service procedures by search query and category.
 * Search matches against itemName or category (case-insensitive).
 */
export function filterServiceProcedures(
    items: ServiceProcedure[],
    searchQuery: string,
    categoryFilter: string,
): ServiceProcedure[] {
    let filtered = items;

    const query = searchQuery.trim().toLowerCase();
    if (query) {
        filtered = filtered.filter((item) => {
            const name = (item.itemName ?? '').toLowerCase();
            const category = (item.category ?? '').toLowerCase();
            return name.includes(query) || category.includes(query);
        });
    }

    if (categoryFilter) {
        filtered = filtered.filter((item) => item.category === categoryFilter);
    }

    return filtered;
}

export function procedureCategoryName(item: ServiceProcedure): string {
    return item.category?.trim() || 'Uncategorized';
}

function categorySortKey(items: ServiceProcedure[]): number {
    let min = Number.POSITIVE_INFINITY;
    for (const item of items) {
        if (item.categorySortOrder == null) continue;
        if (item.categorySortOrder < min) min = item.categorySortOrder;
    }
    return min;
}

function procedureAddedAt(item: ServiceProcedure): number {
    const parsed = item.createdAt ? Date.parse(item.createdAt) : Number.NaN;
    return Number.isFinite(parsed) ? parsed : Number.POSITIVE_INFINITY;
}

/** Oldest first, so a category stays in the order procedures were added. */
function compareProceduresByAddedOrder(a: ServiceProcedure, b: ServiceProcedure): number {
    const added = procedureAddedAt(a) - procedureAddedAt(b);
    if (added !== 0) return added;
    return a.itemName.localeCompare(b.itemName, undefined, { numeric: true, sensitivity: 'base' });
}

/**
 * Groups procedures by category and sorts categories by saved order.
 * Unordered categories (no categorySortOrder) sort alphabetically after ordered ones.
 * Procedures inside a category stay in the order they were added.
 */
export function groupProceduresByCategory(items: ServiceProcedure[]): [string, ServiceProcedure[]][] {
    const grouped: Record<string, ServiceProcedure[]> = {};
    for (const item of items) {
        const cat = procedureCategoryName(item);
        if (!grouped[cat]) grouped[cat] = [];
        grouped[cat].push(item);
    }
    for (const catItems of Object.values(grouped)) {
        catItems.sort(compareProceduresByAddedOrder);
    }
    return Object.entries(grouped).sort(([a, aItems], [b, bItems]) => {
        const orderA = categorySortKey(aItems);
        const orderB = categorySortKey(bItems);
        if (orderA !== orderB) return orderA - orderB;
        return a.localeCompare(b);
    });
}

/** Existing category keeps its order; a new category appends after the highest saved order. */
export function nextCategorySortOrder(items: ServiceProcedure[], categoryName: string): number | null {
    const existing = items.filter((item) => procedureCategoryName(item) === categoryName);
    if (existing.length > 0) {
        const key = categorySortKey(existing);
        return Number.isFinite(key) ? key : null;
    }
    const orders = items
        .map((item) => item.categorySortOrder)
        .filter((order): order is number => order != null);
    if (orders.length === 0) return null;
    return Math.max(...orders) + 1;
}

export function applyCategorySortOrder(
    items: ServiceProcedure[],
    orderedCategoryNames: string[],
): ServiceProcedure[] {
    const orderByName = new Map(orderedCategoryNames.map((name, index) => [name, index]));
    return items.map((item) => {
        const order = orderByName.get(procedureCategoryName(item));
        if (order === undefined) return item;
        return { ...item, categorySortOrder: order };
    });
}

/**
 * Filters vendors by their archived status.
 * 'active' returns vendors where archived is falsy.
 * 'archived' returns vendors where archived is true.
 */
export function filterVendorsByStatus<T extends { archived?: boolean | null }>(
    vendors: T[],
    filter: 'active' | 'archived'
): T[] {
    if (filter === 'active') {
        return vendors.filter(v => !v.archived);
    }
    return vendors.filter(v => v.archived === true);
}

/** Alphabetical vendor list (case-insensitive). */
export function sortVendorsByName<T extends { name?: string | null }>(vendors: T[]): T[] {
    return [...vendors].sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }),
    );
}

// ===== itemType normalization =====
// Canonical values: 'Consumable' | 'Procedure'
// Legacy value 'Action' is treated as 'Procedure'.

/**
 * Returns true if the raw itemType string represents a procedure.
 * Handles legacy 'Action' value and case-insensitive comparison.
 */
export function isProcedureType(itemType?: string | null): boolean {
    if (!itemType) return false;
    const normalized = itemType.toLowerCase();
    return normalized === 'procedure' || normalized === 'action';
}

/**
 * Normalizes a raw itemType value to one of the two canonical values.
 * 'Action' (legacy) → 'Procedure'. Anything else defaults to 'Consumable'.
 */
export function normalizeItemType(itemType?: string | null): 'Consumable' | 'Procedure' {
    return isProcedureType(itemType) ? 'Procedure' : 'Consumable';
}

/**
 * Returns the display label for an itemType value.
 * Procedures (and legacy Action) show as Procedure; other values keep their stored label (e.g. Part).
 */
export function getItemTypeLabel(itemType?: string | null): string {
    if (!itemType?.trim()) return '';
    if (isProcedureType(itemType)) return 'Procedure';
    return itemType.trim();
}

/**
 * Catalog rows that are consumable parts (not procedure steps).
 */
export function consumableCatalogItems(items: PMPartsAndProcedure[]): PMPartsAndProcedure[] {
    return items.filter((item) => !isProcedureType(item.itemType) && Boolean(item.itemName?.trim()));
}

/**
 * Resources → Service Items. Procedure rows, including legacy Action, stay on the Procedures tab.
 */
export function resourcesServiceItems(items: PMPartsAndProcedure[]): PMPartsAndProcedure[] {
    return items.filter((item) => !isProcedureType(item.itemType));
}

const serviceItemListingCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

function listingSortText(value?: string | null): string {
    return (value || '').trim();
}

/** Named values sort first. Blank manufacturer, part, or vendor stays at the end of that level. */
function compareListingText(left?: string | null, right?: string | null): number {
    const a = listingSortText(left);
    const b = listingSortText(right);
    if (!a && !b) return 0;
    if (!a) return 1;
    if (!b) return -1;
    return serviceItemListingCollator.compare(a, b);
}

export function serviceItemVendorSortName(
    item: Pick<PMPartsAndProcedure, 'vendorId' | 'vendorName'>,
    vendors: { id?: string | null; name?: string | null }[] = [],
): string {
    const stored = item.vendorName?.trim();
    if (stored) return stored;
    if (!item.vendorId) return '';
    return vendors.find((vendor) => vendor.id === item.vendorId)?.name?.trim() || '';
}

/**
 * Manufacturer, then part number, then vendor.
 * Extra vendors for the same manufacturer and part stay together before the next manufacturer.
 */
export function compareServiceItemsForListing(
    a: PMPartsAndProcedure,
    b: PMPartsAndProcedure,
    vendors: { id?: string | null; name?: string | null }[] = [],
): number {
    return (
        compareListingText(a.manufacturer, b.manufacturer) ||
        compareListingText(a.partNumber, b.partNumber) ||
        compareListingText(serviceItemVendorSortName(a, vendors), serviceItemVendorSortName(b, vendors)) ||
        compareListingText(a.itemName, b.itemName)
    );
}

export function sortServiceItemsForListing<T extends PMPartsAndProcedure>(
    items: T[],
    vendors: { id?: string | null; name?: string | null }[] = [],
): T[] {
    return [...items].sort((left, right) => compareServiceItemsForListing(left, right, vendors));
}

/** Matrix row that already represents this Resources procedure, matched by name. */
export function findMatrixProcedure(
    items: PMPartsAndProcedure[],
    itemName: string,
): PMPartsAndProcedure | undefined {
    const needle = itemName.trim().toLowerCase();
    if (!needle) return undefined;
    return items.find(
        (item) => isProcedureType(item.itemType) && item.itemName.trim().toLowerCase() === needle,
    );
}

/**
 * Parses a flat-rate quantity typed by the user. Empty or non-positive values are invalid.
 */
export function parseProcedureConsumableQuantity(raw: string): number | null {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    const value = Number(trimmed);
    if (!Number.isFinite(value) || value <= 0) return null;
    return value;
}

/** Display a stored quantity without trailing float noise. */
export function formatProcedureConsumableQuantity(quantity?: number | null): string {
    if (quantity == null || !Number.isFinite(quantity)) return '';
    return String(quantity);
}

/** e.g. "20 oz" — quantity plus the Service Item's pricing UM. */
export function procedureConsumableRateLabel(
    quantity?: number | null,
    unitOfMeasure?: string | null,
): string {
    const qty = formatProcedureConsumableQuantity(quantity);
    if (!qty) return '—';
    const um = unitOfMeasure?.trim();
    return um ? `${qty} ${um}` : qty;
}

/**
 * Resolves join records for one procedure against the Service Items catalog.
 * Missing catalog rows still return so the UI can unlink them.
 */
export function linkedConsumablesForProcedure(
    links: ServiceProcedureConsumable[],
    catalog: PMPartsAndProcedure[],
    procedureId: string,
): { link: ServiceProcedureConsumable; item: PMPartsAndProcedure | null }[] {
    const catalogById = new Map(catalog.map((item) => [item.id, item]));
    return links
        .filter((link) => link.procedureId === procedureId)
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.id.localeCompare(b.id))
        .map((link) => ({
            link,
            item: catalogById.get(link.serviceItemId) ?? null,
        }));
}

/**
 * Groups company resources by category, returning an array of category sections
 * with name, color, items, and count for each RESOURCE_CATEGORIES entry.
 */
export function groupResourcesByCategory(
    resources: CompanyResource[]
): { name: CompanyResourceCategory; color: string; items: CompanyResource[]; count: number }[] {
    return RESOURCE_CATEGORIES.map(cat => {
        const items = resources.filter(r => r.category === cat.name);
        return {
            name: cat.name,
            color: cat.color,
            items,
            count: items.length,
        };
    });
}
